/**
 * ServiciosUsoPanel — pestaña «Todos los servicios» de /admin/uso-ia (solo admin).
 *
 * Consumo y costo de todo lo que usa la teleconsulta además de los modelos de
 * IA: videollamadas (LiveKit), voz, infraestructura de Google, registro
 * biométrico (AWS) y costos fijos. Lee servicios_uso_dia (rango), servicios_uso_hora
 * (últimas 48 h) y consumo_planes (plan de LiveKit del mes). Cada consulta usa un
 * solo rango —no hay índices compuestos— y el resto se filtra en memoria.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Coins, Info, Loader2, RefreshCw, Server, Fingerprint } from 'lucide-react';
import dashStyles from '../../pages/admin/AdminDashboardPage.module.css';
import { db, functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import {
  FUENTE_COSTO_LABELS, categoriaLabel, fmtCosto, fmtCostoOpc, fmtCantidadServicio,
} from '../../utils/usoIaFormato';
import {
  CATEGORIAS, CATEGORIA_COLORES, panamaHoy, periodoHoraHace,
  type ConsumoPlanDoc, type ServicioUsoDoc,
} from './costosUtil';
import {
  agruparPorServicio, awsPorDia, minutosPorRol, prepararDias, resumirCostos,
} from './serviciosAgregado';
import RangoFechasBar from './RangoFechasBar';
import { useRangoFechas } from './useRangoFechas';
import PlanLivekitSeccion from './PlanLivekitSeccion';
import ServiciosPorHora from './ServiciosPorHora';
import ConfigCostosForm from './ConfigCostosForm';
import TooltipGrafico from './TooltipGrafico';
import styles from './ServiciosUsoPanel.module.css';

const MARCA_CLASE: Record<string, string> = {
  estimado: styles.marcaEstimado,
  facturado: styles.marcaFacturado,
  fijo: styles.marcaFijo,
};

const formatoDia = (f: string) => { const [, m, d] = f.split('-'); return `${d}/${m}`; };

interface EstadoCarga { dia: boolean; hora: boolean; plan: boolean }

export default function ServiciosUsoPanel() {
  const { addToast } = useToast();
  const rango = useRangoFechas('30d');
  const { startFecha, endFecha } = rango;

  const [docsDia, setDocsDia] = useState<ServicioUsoDoc[]>([]);
  const [docsHora, setDocsHora] = useState<ServicioUsoDoc[]>([]);
  const [plan, setPlan] = useState<ConsumoPlanDoc | null>(null);
  const [cargando, setCargando] = useState<EstadoCarga>({ dia: true, hora: true, plan: true });
  const [errores, setErrores] = useState<EstadoCarga>({ dia: false, hora: false, plan: false });
  const [actualizando, setActualizando] = useState(false);
  const peticion = useRef(0);

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setCargando({ dia: true, hora: true, plan: true });
    const mes = panamaHoy().slice(0, 7);
    const [dia, hora, planSnap] = await Promise.allSettled([
      getDocs(query(
        collection(db, 'servicios_uso_dia'),
        where('fecha', '>=', startFecha),
        where('fecha', '<=', endFecha),
      )),
      getDocs(query(collection(db, 'servicios_uso_hora'), where('periodo', '>=', periodoHoraHace(48)))),
      getDoc(doc(db, 'consumo_planes', `livekit_${mes}`)),
    ]);
    if (id !== peticion.current) return; // llegó una petición más nueva
    if (dia.status === 'fulfilled') setDocsDia(dia.value.docs.map((d) => d.data() as ServicioUsoDoc));
    else { console.error('[ServiciosUsoPanel] servicios_uso_dia', dia.reason); setDocsDia([]); }
    if (hora.status === 'fulfilled') setDocsHora(hora.value.docs.map((d) => d.data() as ServicioUsoDoc));
    else { console.error('[ServiciosUsoPanel] servicios_uso_hora', hora.reason); setDocsHora([]); }
    if (planSnap.status === 'fulfilled') setPlan(planSnap.value.exists() ? (planSnap.value.data() as ConsumoPlanDoc) : null);
    else { console.error('[ServiciosUsoPanel] consumo_planes', planSnap.reason); setPlan(null); }
    setErrores({ dia: dia.status === 'rejected', hora: hora.status === 'rejected', plan: planSnap.status === 'rejected' });
    setCargando({ dia: false, hora: false, plan: false });
  }, [startFecha, endFecha]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function actualizarAhora() {
    setActualizando(true);
    try {
      const res = await httpsCallable<unknown, { fuentes?: Record<string, { estado: string; mensaje?: string | null }> }>(
        functions, 'portal_costosEjecutarColectores', { timeout: 150_000 },
      )({});
      const fuentes = Object.entries(res.data?.fuentes ?? {});
      const mal = fuentes.filter(([, v]) => v.estado !== 'ok');
      if (mal.length === 0) addToast('success', 'Consumo actualizado: todas las fuentes respondieron bien.');
      else addToast('warning', `Consumo actualizado, pero ${mal.length} ${mal.length === 1 ? 'fuente no respondió' : 'fuentes no respondieron'} bien. Revise el aviso de arriba.`);
      await cargar();
    } catch (err) {
      console.error('[ServiciosUsoPanel] portal_costosEjecutarColectores', err);
      addToast('error', 'No se pudo actualizar el consumo. Intente de nuevo en unos minutos.');
    } finally {
      setActualizando(false);
    }
  }

  const filas = useMemo(() => prepararDias(docsDia), [docsDia]);
  const resumen = useMemo(() => resumirCostos(filas), [filas]);
  const servicios = useMemo(() => agruparPorServicio(filas), [filas]);
  const roles = useMemo(() => minutosPorRol(filas), [filas]);
  const aws = useMemo(() => awsPorDia(filas), [filas]);
  const totalAws = aws.reduce((s, x) => s + x.costo, 0);

  const datosGrafica = useMemo(
    () => [...resumen.porDia.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([fecha, cats]) => ({
      fecha, dia: formatoDia(fecha),
      ...Object.fromEntries(CATEGORIAS.map((c) => [categoriaLabel(c), Number((cats[c] ?? 0).toFixed(4))])),
    })),
    [resumen],
  );

  const sinPrecio = servicios.filter((s) => s.costo == null).length;
  const formatoGrafica = (_n: string, v: number) => fmtCosto(v);

  return (
    <div className={styles.stack}>
      {/* ─── Rango + actualizar ─── */}
      <div className={styles.toolbar}>
        <RangoFechasBar rango={rango} />
        <button type="button" className={styles.accionBtn} onClick={actualizarAhora} disabled={actualizando}>
          {actualizando
            ? <Loader2 size={14} className={dashStyles.spinning} aria-hidden="true" />
            : <RefreshCw size={14} aria-hidden="true" />}
          {actualizando ? 'Actualizando… puede tardar hasta 30 s' : 'Actualizar ahora'}
        </button>
      </div>

      <div className={styles.nota} role="note">
        <Info size={14} aria-hidden="true" />
        <span>
          <strong>Google se estima con precios de lista y sin cupos gratuitos</strong>, así que normalmente
          sobreestima. La factura real depende de la exportación de facturación a BigQuery (se configura abajo);
          cuando llega, reemplaza a la estimación de ese día. Los modelos de IA se ven en la pestaña
          «Inteligencia artificial».
        </span>
      </div>

      {errores.dia ? (
        <p className={styles.errorBox} role="alert">No se pudo leer el consumo por servicio. Pruebe «Actualizar ahora» o recargue la página.</p>
      ) : cargando.dia && docsDia.length === 0 ? (
        <div className={dashStyles.loadingWrap}><Loader2 size={32} /><span>Cargando consumo de servicios...</span></div>
      ) : (
        <>
          {/* ─── KPIs ─── */}
          <div className={dashStyles.kpiGrid}>
            <div className={dashStyles.kpiCard}>
              <div className={dashStyles.kpiCardAccent} style={{ background: 'var(--color-primary)' }} />
              <div className={dashStyles.kpiHeader}>
                <p className={dashStyles.kpiLabel}>Costo total del rango</p>
                <div className={dashStyles.kpiIcon} style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
                  <Coins size={16} aria-hidden="true" />
                </div>
              </div>
              <p className={dashStyles.kpiValue}>{docsDia.length === 0 ? '—' : fmtCosto(resumen.total)}</p>
              <p className={dashStyles.kpiMeta}>{startFecha} a {endFecha}</p>
            </div>
            {CATEGORIAS.map((c) => (
              <div key={c} className={dashStyles.kpiCard}>
                <div className={dashStyles.kpiCardAccent} style={{ background: CATEGORIA_COLORES[c] }} />
                <div className={dashStyles.kpiHeader}>
                  <p className={dashStyles.kpiLabel}>{categoriaLabel(c)}</p>
                </div>
                <p className={dashStyles.kpiValue}>
                  {servicios.some((s) => s.categoria === c && s.costo != null) ? fmtCosto(resumen.porCategoria[c] ?? 0) : '—'}
                </p>
                <p className={dashStyles.kpiMeta}>
                  {servicios.some((s) => s.categoria === c) ? `${servicios.filter((s) => s.categoria === c).length} servicios` : 'sin datos en el rango'}
                </p>
              </div>
            ))}
          </div>

          {resumen.diasSuperados > 0 && (
            <div className={`${styles.nota} ${styles.notaSuave}`}>
              <Info size={14} aria-hidden="true" />
              <span>
                En {resumen.diasSuperados} {resumen.diasSuperados === 1 ? 'día' : 'días'} ya llegó la factura real de
                Google: para esos días se usa la factura y no se suma la estimación, para no contar el mismo gasto dos veces.
              </span>
            </div>
          )}

          {/* ─── Costo por día ─── */}
          <div className={dashStyles.section}>
            <div className={dashStyles.sectionHeader}>
              <h2 className={dashStyles.sectionTitle}><Server size={16} aria-hidden="true" /> Costo por día y categoría</h2>
            </div>
            <div className={dashStyles.sectionBody}>
              {datosGrafica.length === 0 ? (
                <p className={dashStyles.emptyState}>Aún no hay datos de servicios en este rango: se llenan cada hora.</p>
              ) : (
                <div className={dashStyles.chartWrap} role="img"
                  aria-label={`Costo por día entre ${startFecha} y ${endFecha}, apilado por categoría. Total: ${fmtCosto(resumen.total)}.`}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosGrafica} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                      <XAxis dataKey="dia" tick={{ fontSize: 11 }} minTickGap={12} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCosto(v)} width={62} />
                      <Tooltip
                        content={<TooltipGrafico formato={formatoGrafica} titulo={(d) => {
                          const f = datosGrafica.find((x) => x.dia === d);
                          return f ? f.fecha : d;
                        }} />}
                        cursor={{ fill: 'var(--color-surface-muted)' }}
                      />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      {CATEGORIAS.map((c) => (
                        <Bar key={c} dataKey={categoriaLabel(c)} stackId="cat" fill={CATEGORIA_COLORES[c]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* ─── Tabla por servicio ─── */}
          <div className={dashStyles.section}>
            <div className={dashStyles.sectionHeader}>
              <h2 className={dashStyles.sectionTitle}>Detalle por servicio</h2>
              <span className={dashStyles.sectionBadge}>{servicios.length}</span>
            </div>
            <div className={dashStyles.sectionBody}>
              {servicios.length === 0 ? (
                <p className={dashStyles.emptyState}>Aún no hay datos de servicios en este rango: se llenan cada hora.</p>
              ) : (
                <>
                  <div className={dashStyles.tableWrap}>
                    <table className={dashStyles.table}>
                      <caption className={styles.soloLector}>
                        Cantidad y costo por servicio entre {startFecha} y {endFecha}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">Servicio</th>
                          <th scope="col">Categoría</th>
                          <th scope="col" className={styles.num}>Cantidad</th>
                          <th scope="col" className={styles.num}>Costo</th>
                          <th scope="col">Origen del costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {servicios.map((s) => (
                          <tr key={s.clave}>
                            <td className={styles.nombreCelda}>{s.nombre}</td>
                            <td>{categoriaLabel(s.categoria)}</td>
                            <td className={styles.num}>{fmtCantidadServicio(s.cantidad, s.unidad)}</td>
                            <td className={`${styles.num} ${s.costo == null ? styles.sinPrecio : ''}`}
                              title={s.costo == null ? 'Sin costo calculable para este servicio' : undefined}>
                              {fmtCostoOpc(s.costo)}
                            </td>
                            <td>
                              <span className={`${styles.marca} ${MARCA_CLASE[s.marca] ?? ''}`}>
                                {FUENTE_COSTO_LABELS[s.marca] ?? s.marca}
                              </span>
                              {s.diasSuperados > 0 && (
                                <span className={styles.muted}> · {s.diasSuperados} d. reemplazados por factura</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sinPrecio > 0 && (
                    <div className={`${styles.nota} ${styles.notaSuave}`} style={{ marginTop: 'var(--space-3)' }}>
                      <Info size={14} aria-hidden="true" />
                      <span>
                        «—» significa que el servicio se mide pero su costo no se puede calcular por sí solo (por ejemplo,
                        los minutos de LiveKit se cobran como excedente del plan, que aparece en su propia fila). No es $0.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ─── LiveKit ─── */}
      <PlanLivekitSeccion plan={plan} cargando={cargando.plan} roles={roles} desde={startFecha} hasta={endFecha} />
      {errores.plan && <p className={styles.errorBox} role="alert">No se pudo leer el consumo del plan de LiveKit.</p>}

      {/* ─── AWS ─── */}
      <div className={dashStyles.section}>
        <div className={dashStyles.sectionHeader}>
          <h2 className={dashStyles.sectionTitle}><Fingerprint size={16} aria-hidden="true" /> Registro biométrico (AWS)</h2>
          <span className={dashStyles.sectionBadge}>{aws.length > 0 ? `${fmtCosto(totalAws)} en el rango` : 'sin datos'}</span>
        </div>
        <div className={dashStyles.sectionBody}>
          {aws.length === 0 ? (
            <p className={dashStyles.emptyState}>Aún no hay datos de este servicio en el rango: se llenan una vez al día.</p>
          ) : (
            <div className={dashStyles.chartWrap} role="img"
              aria-label={`Costo diario de AWS entre ${startFecha} y ${endFecha}. Total: ${fmtCosto(totalAws)}.`}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={aws.map((a) => ({ dia: formatoDia(a.fecha), fecha: a.fecha, 'Costo AWS': Number(a.costo.toFixed(4)) }))}
                  margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} minTickGap={12} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCosto(v)} width={62} />
                  <Tooltip content={<TooltipGrafico formato={formatoGrafica} />} cursor={{ fill: 'var(--color-surface-muted)' }} />
                  <Bar dataKey="Costo AWS" fill={CATEGORIA_COLORES.biometrico} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className={styles.muted} style={{ marginTop: 'var(--space-2)' }}>
            Costo facturado por AWS (reconocimiento facial, lectura de documentos…). Llega con un día de atraso.
          </p>
        </div>
      </div>

      {/* ─── Por hora ─── */}
      <ServiciosPorHora docs={docsHora} cargando={cargando.hora} error={errores.hora} />

      {/* ─── Configuración ─── */}
      <ConfigCostosForm />
    </div>
  );
}
